import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tag, AlertTriangle, ChevronDown } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { fmtMoney } from "@/lib/dates";

// =============================================================================
// QuoteBudgetStrip — 28 Oct 2026 (mig 473)
//
// James: "a way for me to allocate quote lines quickly to a budget code will
// help build the foundation". Three pieces, all reading the same data:
//
//   useBudgetCodes()   the active project codes (budget_codes, applies_to has
//                      'project'), cached once per screen
//   BudgetCodeChip     the chip on every quote line — click, pick, done
//   SectionCodeMenu    "code the whole section as …" in a section header
//   QuotedBudgetStrip  one row per code: lines · sell · cost · budget. The
//                      budget is cost, or sell × the cost target (75%) where a
//                      line was never costed — v_quote_budget_summary, the
//                      same numbers Mark-won writes into the project budget.
// =============================================================================

export interface BudgetCodeRow {
  budget_code: string;
  display_name: string | null;
  category: string | null;
  meaning: string | null;
  applies_to: string[] | null;
  is_active: boolean;
  sort_order: number | null;
}

export function useBudgetCodes() {
  return useQuery({
    queryKey: ["budget_codes", "project", "active"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("budget_codes")
        .select("budget_code, display_name, category, meaning, applies_to, is_active, sort_order")
        .eq("is_active", true)
        .order("sort_order").order("budget_code");
      if (error) throw new Error(error.message);
      return ((data ?? []) as BudgetCodeRow[]).filter(c => !c.applies_to || c.applies_to.length === 0 || c.applies_to.includes("project"));
    },
  });
}

/** The colour a code wears everywhere on the screen — the bar, the strip, the lines. Stable: by its place in the active list. */
export const CODE_COLOURS = ["#f97316", "#38bdf8", "#a78bfa", "#34d399", "#fbbf24", "#f472b6", "#60a5fa", "#2dd4bf", "#fb7185", "#c084fc", "#facc15", "#4ade80"];
export function codeColour(code: string | null | undefined, codes: { budget_code: string }[]): string {
  if (!code) return "#ef4444";
  const i = codes.findIndex(c => c.budget_code === code);
  if (i >= 0) return CODE_COLOURS[i % CODE_COLOURS.length];
  let h = 0; for (const ch of code) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CODE_COLOURS[h % CODE_COLOURS.length];
}

/** Short form of a code for a chip: "PX01_SUBBIES" → "PX01", "1010-LAB" → "1010". */
export function shortCode(code: string | null | undefined): string {
  if (!code) return "—";
  const m = code.match(/^([A-Za-z]{1,3}\d{1,4}|\d{3,5})/);
  return m ? m[1].toUpperCase() : code.slice(0, 6).toUpperCase();
}

// -----------------------------------------------------------------------------
// Chip on a line
// -----------------------------------------------------------------------------
export function BudgetCodeChip({ value, canEdit, onChange, tint }: {
  value: string | null;
  canEdit: boolean;
  onChange: (code: string) => void;
  /** colour the chip with the code's colour (the "colour by code" toggle) */
  tint?: boolean;
}) {
  const { data: codes = [] } = useBudgetCodes();
  const tintStyle = tint && value ? { borderColor: codeColour(value, codes), color: codeColour(value, codes), background: codeColour(value, codes) + "1a" } : undefined;
  const row = codes.find(c => c.budget_code === value);
  const label = row?.display_name ?? value ?? "no code";
  const missing = !value || (!row && codes.length > 0 && !value);

  if (!canEdit) {
    return (
      <span className={`inline-flex items-center gap-1 font-mono text-[10px] rounded px-1.5 py-0.5 border ${missing ? "border-danger/40 text-danger" : "border-line text-text-muted"}`} style={tintStyle} title={label}>
        <Tag size={9}/>{shortCode(value)}
      </span>
    );
  }
  return (
    <label className={`relative inline-flex items-center gap-1 font-mono text-[10px] rounded px-1.5 py-0.5 border cursor-pointer hover:border-accent ${missing ? "border-danger/40 text-danger" : "border-line text-text-muted hover:text-text"}`} style={tintStyle} title={`Budget code — ${label}. Click to change.`}>
      <Tag size={9}/>{shortCode(value)}<ChevronDown size={9}/>
      <select
        value={value ?? ""}
        onChange={e => { if (e.target.value) onChange(e.target.value); }}
        onClick={e => e.stopPropagation()}
        className="absolute inset-0 opacity-0 cursor-pointer w-full"
        aria-label="Budget code"
      >
        <option value="">— pick a budget code —</option>
        {codes.map(c => (
          <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name ?? ""}</option>
        ))}
      </select>
    </label>
  );
}

// -----------------------------------------------------------------------------
// Section header — code every line in the section at once
// -----------------------------------------------------------------------------
export function SectionCodeMenu({ lines, onSet }: {
  lines: { budget_code?: string | null }[];
  onSet: (code: string) => void;
}) {
  const { data: codes = [] } = useBudgetCodes();
  const distinct = useMemo(() => [...new Set(lines.map(l => l.budget_code ?? ""))].filter(Boolean), [lines]);
  const summary = (distinct.length === 0 ? "no code" : distinct.length === 1 ? shortCode(distinct[0]) : `${distinct.length} codes`) + " · set all";
  return (
    <label
      className="relative inline-flex items-center gap-1 font-mono text-[10px] text-text-muted border border-line rounded px-1.5 py-0.5 cursor-pointer hover:border-accent hover:text-text"
      title="Set the budget code on every line in this section"
      onClick={e => e.stopPropagation()}
    >
      <Tag size={9}/>{summary}<ChevronDown size={9}/>
      <select
        value=""
        onChange={e => { if (e.target.value) onSet(e.target.value); }}
        className="absolute inset-0 opacity-0 cursor-pointer w-full"
        aria-label="Code the whole section"
      >
        <option value="">Code the whole section as…</option>
        {codes.map(c => (
          <option key={c.budget_code} value={c.budget_code}>{c.budget_code} · {c.display_name ?? ""}</option>
        ))}
      </select>
    </label>
  );
}

// -----------------------------------------------------------------------------
// The strip
// -----------------------------------------------------------------------------
export interface QuoteBudgetSummaryRow {
  quote_id: string;
  budget_code: string | null;
  code_label: string;
  n_lines: number;
  n_uncosted: number;
  cost_ex: number;
  sell_ex: number;
  budget_ex: number;
  sort_order: number;
}

export function useQuoteBudgetSummary(quoteId: string | undefined) {
  return useQuery({
    queryKey: ["quote_budget_summary", quoteId],
    enabled: !!quoteId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("v_quote_budget_summary")
        .select("*")
        .eq("quote_id", quoteId)
        .order("sort_order").order("budget_code");
      if (error) throw new Error(error.message);
      return (data ?? []) as QuoteBudgetSummaryRow[];
    },
  });
}

export function useCostTargetPct() {
  return useQuery({
    queryKey: ["finance_settings", "project_target_cost_pct"],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data } = await (supabase as any).from("finance_settings").select("value").eq("key", "project_target_cost_pct").maybeSingle();
      const n = Number(data?.value);
      return Number.isFinite(n) && n > 0 ? n : 75;
    },
  });
}

export interface StripLine { quote_line_id: string; line_number: number | null; description: string; section_name: string; budget_code: string | null; cost: number; sell: number; is_optional: boolean }

export function QuotedBudgetStrip({ quoteId, compact = false, lines, canRemap, onRemap }: {
  quoteId: string | undefined; compact?: boolean;
  /** the quote's lines — a code row opens to show what makes it up, with the chip to re-map (28 Oct 2026: "seeing what is where and what makes up which") */
  lines?: StripLine[]; canRemap?: boolean; onRemap?: (quoteLineId: string, code: string) => void;
}) {
  const { data: rows = [], isLoading, error } = useQuoteBudgetSummary(quoteId);
  const { data: pct = 75 } = useCostTargetPct();
  const { data: stripCodes = [] } = useBudgetCodes();
  const [open, setOpen] = useState(!compact);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const linesFor = (code: string | null) => (lines ?? []).filter(l => !l.is_optional && (l.budget_code ?? null) === code);

  const tot = useMemo(() => rows.reduce((a, r) => ({
    sell: a.sell + Number(r.sell_ex ?? 0),
    cost: a.cost + Number(r.cost_ex ?? 0),
    budget: a.budget + Number(r.budget_ex ?? 0),
    uncosted: a.uncosted + Number(r.n_uncosted ?? 0),
    lines: a.lines + Number(r.n_lines ?? 0),
  }), { sell: 0, cost: 0, budget: 0, uncosted: 0, lines: 0 }), [rows]);
  const marginPct = tot.sell > 0 ? ((tot.sell - tot.budget) / tot.sell) * 100 : null;
  const noCode = rows.find(r => !r.budget_code);

  if (error) return <div className="text-[12px] text-danger">Quoted budget could not load: {(error as Error).message}</div>;
  if (isLoading) return null;
  if (rows.length === 0) return null;

  return (
    <div className={`border border-line rounded-md bg-ink-card ${compact ? "" : "mt-3"}`}>
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <Tag size={13} className="text-accent"/>
        <span className="font-semibold text-[13px]">Quoted budget</span>
        <span className="text-[11px] text-text-muted">
          {rows.length} code{rows.length === 1 ? "" : "s"} · {tot.lines} line{tot.lines === 1 ? "" : "s"}
        </span>
        <span className="ml-auto font-mono text-[12px] text-text">{fmtMoney(tot.budget)}</span>
        {marginPct != null && (
          <span className={`font-mono text-[11px] ${marginPct >= 25 ? "text-ok" : marginPct >= 15 ? "text-warn" : "text-danger"}`}>
            {marginPct.toFixed(0)}% margin
          </span>
        )}
        <ChevronDown size={13} className={`text-text-muted transition-transform ${open ? "" : "-rotate-90"}`}/>
      </button>
      {open && (
        <div className="border-t border-line">
          <AllocationBar rows={rows} tot={tot} pct={pct} />
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider2 text-text-muted">
                <th className="text-left font-semibold px-3 py-1.5">Budget code</th>
                <th className="text-right font-semibold px-2 py-1.5">Lines</th>
                <th className="text-right font-semibold px-2 py-1.5">Sell</th>
                <th className="text-right font-semibold px-2 py-1.5">Cost</th>
                <th className="text-right font-semibold px-3 py-1.5">Budget</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <React.Fragment key={r.budget_code ?? "none"}>
                <tr className={`border-t border-line/60 ${lines ? "cursor-pointer hover:bg-ink-raised/60" : ""}`} onClick={() => lines && setOpenCode(c => c === (r.budget_code ?? "none") ? null : (r.budget_code ?? "none"))}>
                  <td className="px-3 py-1.5">
                    {lines && <ChevronDown size={11} className={`inline mr-1 text-text-dim transition-transform ${openCode === (r.budget_code ?? "none") ? "" : "-rotate-90"}`}/>}
                    <span className="inline-block w-2 h-2 rounded-sm mr-1.5 align-middle" style={{ background: codeColour(r.budget_code, stripCodes) }}/>
                    <span className={`font-mono text-[10px] mr-2 ${r.budget_code ? "text-accent" : "text-danger"}`}>{r.budget_code ?? "NO CODE"}</span>
                    <span className="text-text">{r.budget_code ? r.code_label : "lines with no budget code"}</span>
                  </td>
                  <td className="text-right font-mono px-2 py-1.5 text-text-muted">{r.n_lines}</td>
                  <td className="text-right font-mono px-2 py-1.5">{fmtMoney(r.sell_ex)}</td>
                  <td className="text-right font-mono px-2 py-1.5">
                    {Number(r.cost_ex) > 0 ? fmtMoney(r.cost_ex) : <span className="text-text-dim">—</span>}
                    {r.n_uncosted > 0 && Number(r.cost_ex) > 0 && <span className="text-warn ml-1" title={`${r.n_uncosted} line(s) with no cost`}>·{r.n_uncosted}</span>}
                  </td>
                  <td className="text-right font-mono px-3 py-1.5 text-text">
                    {fmtMoney(r.budget_ex)}
                    {r.n_uncosted > 0 && <span className="text-warn ml-1" title={`${r.n_uncosted} uncosted line(s) budgeted at ${pct}% of sell`}>*</span>}
                  </td>
                </tr>
                {lines && openCode === (r.budget_code ?? "none") && linesFor(r.budget_code).map(l => (
                  <tr key={l.quote_line_id} className="bg-ink-raised/40">
                    <td className="pl-9 pr-3 py-1 text-[11.5px]" colSpan={2}>
                      <span className="font-mono text-[10px] text-text-dim mr-2">L{l.line_number ?? "?"}</span>
                      <span className="text-text-muted">{l.section_name} · </span>
                      <span className="text-text">{(l.description || "(no description)").split("\n")[0].slice(0, 90)}</span>
                    </td>
                    <td className="text-right font-mono px-2 py-1 text-[11.5px]">{fmtMoney(l.sell)}</td>
                    <td className="text-right font-mono px-2 py-1 text-[11.5px]">{l.cost > 0 ? fmtMoney(l.cost) : <span className="text-text-dim">—</span>}</td>
                    <td className="text-right px-3 py-1">
                      <span onClick={e => e.stopPropagation()}>
                        <BudgetCodeChip value={l.budget_code} canEdit={!!canRemap && !!onRemap} onChange={code => onRemap?.(l.quote_line_id, code)} />
                      </span>
                    </td>
                  </tr>
                ))}
                </React.Fragment>
              ))}
              <tr className="border-t border-line font-semibold">
                <td className="px-3 py-1.5">Total</td>
                <td className="text-right font-mono px-2 py-1.5 text-text-muted">{tot.lines}</td>
                <td className="text-right font-mono px-2 py-1.5">{fmtMoney(tot.sell)}</td>
                <td className="text-right font-mono px-2 py-1.5">{tot.cost > 0 ? fmtMoney(tot.cost) : "—"}</td>
                <td className="text-right font-mono px-3 py-1.5">{fmtMoney(tot.budget)}</td>
              </tr>
            </tbody>
          </table>
          <div className="px-3 py-2 border-t border-line/60 text-[11px] text-text-muted flex items-start gap-2">
            {(tot.uncosted > 0 || noCode) ? <AlertTriangle size={12} className="text-warn mt-0.5 shrink-0"/> : null}
            <span>
              {noCode && <>Some lines have no budget code — pick one on the line or code the section. </>}
              {tot.uncosted > 0
                ? <>{tot.uncosted} line{tot.uncosted === 1 ? "" : "s"} carry no cost, so their budget is set at <span className="font-mono text-text">{pct}%</span> of sell (*). Type a cost on the line to use the real figure. </>
                : null}
              When this quote is won, the job's budget is {pct}% of what the client pays, split across these codes in this proportion — so the split matters more than the cost figure. Optional lines are left out.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Allocated vs unallocated — sell is the full width; the buckets fill it from
// the left (one colour per code), margin is what is left, a tick marks the
// cost target. Lines with no code show red: they are sell with no bucket.
// -----------------------------------------------------------------------------
function AllocationBar({ rows, tot, pct }: {
  rows: QuoteBudgetSummaryRow[];
  tot: { sell: number; cost: number; budget: number; uncosted: number; lines: number };
  pct: number;
}) {
  const { data: codes = [] } = useBudgetCodes();
  if (tot.sell <= 0) return null;
  const coded = rows.filter(r => r.budget_code);
  const noCodeSell = rows.filter(r => !r.budget_code).reduce((a, r) => a + Number(r.sell_ex ?? 0), 0);
  const allocated = coded.reduce((a, r) => a + Number(r.budget_ex ?? 0), 0);
  const margin = Math.max(0, tot.sell - allocated - noCodeSell);
  const over = Math.max(0, allocated + noCodeSell - tot.sell);
  const w = (v: number) => `${Math.max(0, Math.min(100, (v / tot.sell) * 100))}%`;
  const allocPct = (allocated / tot.sell) * 100;
  return (
    <div className="px-3 pt-3 pb-1">
      <div className="flex items-center justify-between text-[11px] text-text-muted mb-1">
        <span>
          <span className="font-mono text-text">{fmtMoney(allocated)}</span> allocated to buckets
          <span className="font-mono text-text-dim"> · {allocPct.toFixed(0)}% of sell</span>
          {noCodeSell > 0 && <span className="text-danger"> · {fmtMoney(noCodeSell)} with no code</span>}
        </span>
        <span>
          margin <span className={`font-mono ${margin / tot.sell >= 0.25 ? "text-ok" : margin / tot.sell >= 0.15 ? "text-warn" : "text-danger"}`}>{fmtMoney(margin)}</span>
          <span className="font-mono text-text-dim"> · target {100 - pct}%</span>
        </span>
      </div>
      <div className="relative h-4 rounded overflow-hidden bg-ink-raised border border-line flex">
        {coded.map((r) => (
          <div key={r.budget_code!} style={{ width: w(Number(r.budget_ex ?? 0)), background: codeColour(r.budget_code, codes) }}
               title={`${r.budget_code} ${r.code_label} — ${fmtMoney(r.budget_ex)}`} className="h-full"/>
        ))}
        {noCodeSell > 0 && <div style={{ width: w(noCodeSell), background: "repeating-linear-gradient(45deg,#ef4444 0 4px,#7f1d1d 4px 8px)" }} title={`No budget code — ${fmtMoney(noCodeSell)}`} className="h-full"/>}
        <div className="h-full flex-1" title={`Margin — ${fmtMoney(margin)}`}/>
        {/* the cost target tick */}
        <div className="absolute top-0 bottom-0 w-px bg-text" style={{ left: `${pct}%` }} title={`Cost target ${pct}% of sell`}/>
        <div className="absolute -top-0.5 text-[8px] font-mono text-text-muted" style={{ left: `calc(${pct}% + 3px)` }}>{pct}%</div>
      </div>
      {over > 0 && <div className="text-[11px] text-danger mt-1">Budgets are {fmtMoney(over)} more than the sell — this quote loses money as priced.</div>}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px] text-text-muted">
        {coded.map((r) => (
          <span key={r.budget_code!} className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: codeColour(r.budget_code, codes) }}/>
            <span className="font-mono">{shortCode(r.budget_code)}</span> {r.code_label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm border border-line bg-ink-raised"/>margin</span>
      </div>
    </div>
  );
}
